import { getAnalytics, logEvent } from "firebase/analytics";
import { doc, setDoc } from "firebase/firestore";
import { isArray, mergeWith as _mergeWith, MergeWithCustomizer } from "lodash"
import { useEffect, useState } from "react";
import config from "../../config";
import { applyMessagesToData } from "./applyMessagesToData";
import { convertDataToObject } from "./convertDataToObject";
import { uniqueSessionId } from "./useDataStoreFirestore";
import { fbDb } from "./useFirestore";

export function mergeWith<TObject, TSource>(object: TObject, source: TSource, customizer: MergeWithCustomizer): TObject & TSource {
    return _mergeWith(deepCopy(object), source, customizer)
}

export function deepCopy<Type>(source: Type) {
    return JSON.parse(JSON.stringify(source)) as Type
}

export type Message<dataTypes> = { path: string | string[], data: dataTypes, index?: number, remove?: true, ids?: string[] }
export type Messages<dataTypes> = Message<dataTypes> | Message<dataTypes>[]



export function pathEnd(path: string | string[]) {
    if (!isArray(path)) path = path.split('.');
    return path.slice(-1)[0]
}

export function pathUp(path: string | string[], levels: number = 1) {
    if (!isArray(path)) path = path.split('.');
    return path.slice(0, -levels)
}

export function pathCreateObject(path: string | string[], value?: any) {
    if (!isArray(path)) path = path.split('.');
    const pathObject = {} as { [key: string]: any };
    const endObject = pathUp(path).reduce((o: any, i) => {
        return o[i] = {};
    }, pathObject);
    if (value !== undefined) endObject[pathEnd(path)] = value;
    return pathObject;
}

export let miroUserId: string
export let miroTeamId: string
export let miroBoardId: string

async function getMiroTokenData() {
    if (!miroUserId) {
        // @ts-ignore
        let miroTokenData = JSON.parse(atob((await miro.board.getIdToken()).split('.')[1]))
        miroUserId = miroTokenData.user
        miroTeamId = miroTokenData.team;
        miroBoardId = (await miro.board.getInfo()).id
    }
}

export async function getMiroUserId() {
    if (!miroUserId) {
        await getMiroTokenData()
    }
    return miroUserId;
}

export async function getMiroTeamId() {
    if (!miroTeamId) {
        await getMiroTokenData()
    }
    return miroTeamId;
}

let miroClientId: string

export async function getMiroClientId() {
    if (!miroClientId) {
        // @ts-ignore
        miroClientId = JSON.parse(atob((await miro.board.getIdToken()).split('.')[1])).sub
        // @ts-ignore

        // console.log("token Data", JSON.parse(atob((await miro.board.getIdToken()).split('.')[1])));

    }
    return miroClientId;
}

async function mergeDataKey(dataKey: string, addUserId: boolean) {
    if (addUserId) {
        return dataKey + "-" + await getMiroUserId()
    }
    return dataKey
}


export function useDataStoreMiro<Type, dataTypes>(initialState: Type, dataKey: string, addUserId: boolean = false) {
    // Make sure we have a key set
    if (dataKey.length === 0) throw new Error("Key for data store not set");
    const dataPath: string[] = [];
    if (addUserId) {
        dataPath.push(`miroUsers`, miroUserId, `settings`, config.id, "tools", dataKey, `miroBoards`, miroBoardId,);
    } else {
        dataPath.push(`miroBoards`, miroBoardId, `settings`, config.id, "tools", dataKey);
    }

    const [state, setState] = useState<Type>(convertDataToObject<Type>(initialState))

    // Get the data that is already on the Miro Board
    useEffect(() => {
        let cancelled = false;

        async function getInitialDataFromMiro() {
            const mergedDataKey = await mergeDataKey(dataKey, addUserId)
            let miroState: any = await miro.board.getAppData(mergedDataKey);
            if (miroState && !cancelled) {
                versionUpgrader(mergedDataKey, miroState)
                setState(convertDataToObject(miroState) as unknown as Type)
            }
        }

        getInitialDataFromMiro();

        // // Subscribe to partial updates
        // let first = true;
        // const cleanup = onFirestoreSnapshot([...dataPath, "u", "l"], (doc) => {
        //     if (first) {
        //         first = false;
        //         return;
        //     }

        //     if (doc.exists()) {
        //         const action = doc.data();
        //         if (action.id !== uniqueSessionId) {
        //             setState((s) => {
        //                 console.log("s", s);

        //                 const changed = applyMessagesToData(action.messages, convertObjectToData(s))
        //                 return convertDataToObject<any>(changed);
        //             });
        //         }
        //     }
        // })

        return function abort() {
            cancelled = true;
            // cleanup();
        };
    }, []);

    async function dispatch<Type, dataTypes>(
        messages: Messages<dataTypes> | Message<dataTypes>
    ) {
        if (!Array.isArray(messages)) messages = [messages];
        const mergedDataKey = await mergeDataKey(dataKey, addUserId)
        let originalData: any = (await miro.board.getAppData(mergedDataKey)) || initialState;

        const changedData = applyMessagesToData<dataTypes>(messages, originalData);

        // Set local State
        setState(convertDataToObject<any>(changedData));

        // Set external state
        await miro.board.setAppData(mergedDataKey, changedData)

        // // Notify Firestore of state change so other viewer can know about it
        // await setDoc(doc(fbDb, [...dataPath, "u", "l"].join("/")), {
        //     id: uniqueSessionId,
        //     messages: messages
        // });

        return changedData
    }

    return [state, dispatch]
}

export function updateArray<dataTypes>(index: number | undefined, newArray: any[], data: any, many: boolean = false) {
    if (isNumber(index)) {
        newArray.splice(index, 0, data);
    } else if (index === 'remove') {
        // Do nothing, it has already been removed
    } else if (index === 'end') {
        many ?
            newArray.push(...data) :
            newArray.push(data);
    } else {
        newArray.unshift(data);
    }
}

export function versionUpgrader(key: string, data: any) {
    const exactMatchFound = versionExactMatch[key + "|" + data?._v];
    if (exactMatchFound) {
        exactMatchFound(key, data)
    }
}

export const versionExactMatch: { [key: string]: (key: string, data: any) => any } = {
    // "board|undefined": (key: string, data: any) => {
    //     data.colors._gradients._order = Object.values(data.colors._gradients).map((g: any) => g.id)
    //     data._v = "0.0.1";
    //     return data;
    // }
}

function isNumber(test: any): test is number {
    return typeof test === "number";
}


